import { Component, ErrorHandler, Inject, Injector, OnInit, ChangeDetectionStrategy, inject, signal } from '@angular/core'
import { FieldTree, form } from '@angular/forms/signals'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { FirebaseLemonator } from 'app/_angular/service/firebase-lemonator.service'
import { LadataanService } from 'app/_jaettu-angular/service/ladataan.service'
import { Tilikausi } from 'app/_jaettu-lemonator/model/asiakas'
import { ArkistoFile, ArkistoFileTiedostoTyyppi, ArkistoService } from 'app/_jaettu-lemonator/service/arkisto.service'
import { ArkistoFileTiedostoTyyppiNimet } from './kirjanpito-hallinto.component'

export interface ArkistoMuokkaaDialogData {
  file: ArkistoFile
  tilikaudet: Tilikausi[]
}

interface ArkistonMuokkausLomake {
  lisatiedot: string
  tilikausiAvaimet: string[]
  tiedostoTyyppi: ArkistoFileTiedostoTyyppi | null
}

@Component({
  templateUrl: './arkisto-muokkaa.dialog.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ArkistoMuokkaaDialog implements OnInit {
  lomake: FieldTree<ArkistonMuokkausLomake>
  tilikaudet: Tilikausi[]
  fileName: string
  onAutomaationLuoma: boolean
  private _saveStarted = false
  private readonly _injector = inject(Injector)

  readonly kayttajanTiedostotyypit: { arvo: ArkistoFileTiedostoTyyppi; nimi: string }[] = [
    ArkistoFileTiedostoTyyppi.VEROTUS,
    ArkistoFileTiedostoTyyppi.YHTIOPAPERIT,
    ArkistoFileTiedostoTyyppi.LAINAPAPERIT,
    ArkistoFileTiedostoTyyppi.VUOKRASOPIMUKSET,
    ArkistoFileTiedostoTyyppi.KIRJEENVAIHTO,
    ArkistoFileTiedostoTyyppi.TILINTARKASTUS,
    ArkistoFileTiedostoTyyppi.MUUTA,
  ].map(arvo => ({ arvo, nimi: ArkistoFileTiedostoTyyppiNimet[arvo] }))

  constructor(
    @Inject(MAT_DIALOG_DATA) private _data: ArkistoMuokkaaDialogData,
    private _dialogRef: MatDialogRef<ArkistoMuokkaaDialog>,
    private _lemonatorFirebase: FirebaseLemonator,
    private _arkistoService: ArkistoService,
    private _ladataanService: LadataanService,
    private _errorHandler: ErrorHandler,
  ) {}

  ngOnInit() {
    this.tilikaudet = this._data.tilikaudet ?? []
    this.fileName = this._data.file.fileName
    this.onAutomaationLuoma = this._arkistoService.onAutomaationLuomaTilinpaatosdokumentti(this._data.file)

    const olemassaOlevatAvaimet = this._data.file.tilikaudet ?? []

    this.lomake = form(
      signal<ArkistonMuokkausLomake>({
        lisatiedot: this._data.file.lisatiedot ?? '',
        tilikausiAvaimet: olemassaOlevatAvaimet,
        tiedostoTyyppi: this.onAutomaationLuoma ? null : (this._data.file.tiedostoTyyppi ?? null),
      }),
      { injector: this._injector },
    )
  }

  async save() {
    if (this._saveStarted) {
      return
    }
    this._saveStarted = true

    this._ladataanService.aloitaLataaminen()

    try {
      const tilikausiAvaimet = this.lomake.tilikausiAvaimet().value()
      const paivitys: Pick<ArkistoFile, 'tilikaudet' | 'lisatiedot' | 'tiedostoTyyppi'> = {
        tilikaudet: tilikausiAvaimet?.length > 0 ? tilikausiAvaimet : null,
        lisatiedot: this.lomake.lisatiedot().value(),
        tiedostoTyyppi: this.onAutomaationLuoma ? this._data.file.tiedostoTyyppi : (this.lomake.tiedostoTyyppi().value() ?? null),
      }

      const uri = this._arkistoService.getArkistoMetadataUri(this._data.file.asiakasAvain, this._data.file.avain)
      await this._lemonatorFirebase.firestoreUpdateData(uri, paivitys)

      this._dialogRef.close()
    } catch (err) {
      this._errorHandler.handleError(err)
    } finally {
      this._saveStarted = false
      this._ladataanService.lopetaLataaminen()
    }
  }
}
